'use client';
import React, { useState, useEffect, useRef } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Room {
  id: string;
  badge: string;
  image: string;
  alt: string;
  title: string;
  guests: string;
  description: string;
  amenities: string[];
  whatsappMsg: string;
}

interface GalleryImage {
  src: string;
  alt: string;
  aspect: 'square' | 'portrait' | 'landscape';
}

interface Testimonial {
  name: string;
  country: string;
  flag: string;
  text: string;
}

interface FAQ {
  question: string;
  answer: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const ROOMS: Room[] = [
{
  id: 'deluxe-double',
  badge: 'Deluxe Double',
  image: '/assests/images/img7.jpg',
  alt: 'Deluxe Double Room at Shady Mango Villa — comfortable bed with red and white pillows',
  title: 'Deluxe Double Room',
  guests: '2 Guests · Perfect for Couples',
  description: 'A beautifully appointed air-conditioned room featuring a plush double bed, garden views, private bathroom with hot water, and all the comforts of home. Wake up to the sounds of tropical birds and the lush Sigiriya jungle.',
  amenities: ['Air Conditioning', 'Free WiFi', 'Hot Water', 'Garden View', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Deluxe Double Room at Shady Mango Villa. Please share availability."
},
{
  id: 'superior-room',
  badge: 'Superior Room',
  image: '/assests/images/img14.jpg',
  alt: 'Superior Room at Shady Mango Villa — spacious room with double and single bed',
  title: 'Superior Room',
  guests: '3 Guests · Ideal for Families',
  description: 'Spacious and serene, this superior room offers a large double bed plus a single bed — perfect for small families or groups. Enjoy the tropical garden ambiance, natural ventilation, and the warmth of genuine Sri Lankan hospitality.',
  amenities: ['Air Conditioning', 'Free WiFi', 'Hot Water', 'Garden View', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Superior Room at Shady Mango Villa. Please share availability."
},

{
  id: 'family-room',
  badge: 'Double Room',
 image: '/assests/images/img3.jpg',
  alt: 'Family Room at Shady Mango Villa — wooden walls with refrigerator and comfortable furnishings',
  title: 'Deluxe Double Room',
  guests: '4 Guests · Great for Families',
  description: 'Our largest room, designed for families seeking comfort and space. Features two single beds and a large double bed, wooden accents, a refrigerator, and a private bathroom. Surrounded by the lush tropical garden of Sigiriya.',
  amenities: ['Air Conditioning', 'Free WiFi', 'Hot Water', 'Refrigerator', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Deluxe Family Room at Shady Mango Villa. Please share availability."
},

{
  id: 'family-room3',
  badge: 'Family Room',
 image: '/assests/images/img17.jpg',
  alt: 'Family Room at Shady Mango Villa — wooden walls with refrigerator and comfortable furnishings',
  title: 'Deluxe Family Room',
  guests: '4 Guests · Great for Families',
  description: 'Our largest room, designed for families seeking comfort and space. Features two single beds and a large double bed, wooden accents, a refrigerator, and a private bathroom. Surrounded by the lush tropical garden of Sigiriya.',
  amenities: ['Air Conditioning', 'Free WiFi', 'Hot Water', 'Refrigerator', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Deluxe Family Room at Shady Mango Villa. Please share availability."
},
{
  id: 'family-room2',
  badge: 'Family Room',
 image: '/assests/images/img19.jpg',
  alt: 'Family Room at Shady Mango Villa — wooden walls with refrigerator and comfortable furnishings',
  title: 'Deluxe Family Room',
  guests: '4 Guests · Great for Families',
  description: 'Our largest room, designed for families seeking comfort and space. Features two single beds and a large double bed, wooden accents, a refrigerator, and a private bathroom. Surrounded by the lush tropical garden of Sigiriya.',
  amenities: ['Air Conditioning', 'Free WiFi', 'Hot Water', 'Refrigerator', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Deluxe Family Room at Shady Mango Villa. Please share availability."
},
{
  id: 'non-ac-room',
  badge: 'Non-AC Room',
   image: '/assests/images/img18.jpg',
  alt: 'Non-AC Room at Shady Mango Villa — naturally ventilated room with garden views',
  title: 'Garden View Room',
  guests: '2 Guests · Budget-Friendly',
  description: 'Embrace the natural cool breeze of Sigiriya in this charming non-AC room. Naturally ventilated with ceiling fans, garden views, and all essential amenities. The perfect choice for eco-conscious travellers who love nature.',
  amenities: ['Ceiling Fan', 'Free WiFi', 'Hot Water', 'Garden View', 'Private Bathroom', 'Tea & Coffee', 'Free Breakfast', 'Free Parking'],
  whatsappMsg: "Hi! I'd like to book the Garden View Room at Shady Mango Villa. Please share availability."
}];


const GALLERY_IMAGES: GalleryImage[] = [
{ src:  '/assests/images/img13.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img14.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img15.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img12.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img11.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img20.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img2.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img3.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img4.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img5.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img7.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img6.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img8.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'landscape' },
{ src:  '/assests/images/img9.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img10.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img21.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img17.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img18.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img19.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img20.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },
{ src:  '/assests/images/img6.jpg', alt: 'Shady Mango Villa bedroom with red and white pillows', aspect: 'portrait' },

]



const TESTIMONIALS: Testimonial[] = [
{ name: 'Ashok', country: 'Denmark', flag: '🇩🇰', text: 'Nice and comfortable. Very close to the lions rock. Nice and spacious. Nice host, very kind and polite.' },
{ name: 'Michele', country: 'Germany', flag: '🇩🇪', text: 'Very nice, new and clean room, with a nice balcony in front. Very well equipped with fridge and coffee/tea station. Comfy bed. Lovely owner and supercute cats. Plus a delicious breakfast. Would definitely recommend.' },
{ name: 'Luke', country: 'Australia', flag: '🇦🇺', text: 'Beautiful location with lovely gardens and jungle surrounds while still being right next to amazing restaurants and a walking distance to Sigiriya rock. Very friendly family.' },
{ name: 'Kaan', country: 'Turkey', flag: '🇹🇷', text: 'Extremely clean, big room with proper air condition. Very helpful and friendly owners. Breakfast was far the best I had in Sri Lanka.' },
{ name: 'Janine', country: 'Switzerland', flag: '🇨🇭', text: 'Breakfast was super! The family who owned it were very nice and caring people. Room was spacious and clean.' },
{ name: 'Nasrul', country: 'Singapore', flag: '🇸🇬', text: 'Amazing hosts, clean and spacious room and delicious breakfast. Very welcoming and accommodating family.' }];


const FAQS: FAQ[] = [
{ question: 'Where is Shady Mango Villa located?', answer: 'Shady Mango Villa is located at 116/A/01 Kalapuraya, Sigiriya, Sri Lanka — just 1.4 km from the iconic Sigiriya Rock Fortress and within walking distance of local restaurants and attractions.' },
{ question: 'Does Shady Mango Villa serve breakfast?', answer: 'Yes! All stays include a complimentary full breakfast, freshly prepared each morning. Guests rave about our delicious Sri Lankan and Western breakfast options.' },
{ question: 'Is there free parking at Shady Mango Villa?', answer: 'Yes — free private parking is available on-site for all guests. Our property is easily accessible and we are happy to guide you in via WhatsApp.' },
{ question: 'What types of rooms are available?', answer: 'We offer 4 rooms in total: Deluxe Double Room (AC), Superior Room (AC), Deluxe Family Room (AC), and a Garden View Room (non-AC with ceiling fan). All rooms have private bathrooms and garden views.' },
{ question: 'How do I book a room?', answer: 'The easiest way to book is via WhatsApp — tap any "Book on WhatsApp" button to chat directly with us. You can also call us at 0716251476. We respond quickly and will personally confirm your booking.' },
{ question: 'How far is Sigiriya Rock from the villa?', answer: 'Sigiriya Rock Fortress is just 1.4 km from our villa — a short tuk-tuk ride or a pleasant walk through the village. Pidurangala Rock is 4.6 km away and Dambulla Cave Temple is 20 km.' }];


const AMENITIES = [
{ icon: '🍳', label: 'Free Breakfast' },
{ icon: '📶', label: 'Free WiFi' },
{ icon: '🚗', label: 'Free Parking' },
{ icon: '❄️', label: 'Air Conditioning' },
{ icon: '🚿', label: 'Hot Water' },
{ icon: '☕', label: 'Tea & Coffee' },
{ icon: '🍽️', label: 'Restaurant On-site' },
{ icon: '🌿', label: 'Garden & Terrace' },
{ icon: '🧴', label: 'Toiletries' },
{ icon: '🛎️', label: 'Room Service' },
{ icon: '🚌', label: 'Airport Shuttle' },
{ icon: '🔐', label:  'Safe & Secure' }];


const NEARBY = [
{ place: 'Sigiriya Rock Fortress', distance: '~1.4 km' },
{ place: 'Pidurangala Rock', distance: '~4.6 km' },
{ place: 'Dambulla Cave Temple', distance: '~20 km' },
{ place: 'Sigiriya Village', distance: '~0.5 km' },
{ place: 'Local Restaurants', distance: 'Walking distance' },
{ place: 'Sigiriya Airport', distance: '~11 km' }];


// ─── Utility ─────────────────────────────────────────────────────────────────
function useIntersection(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) {setVisible(true);obs.disconnect();}},
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function WhatsAppIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>);

}

function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>);

}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>);

}

function ChevronIcon({ open }: {open: boolean;}) {
  return (
    <svg
      width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
      aria-hidden="true">
      
      <polyline points="6 9 12 15 18 9" />
    </svg>);

}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function ShadyMangoVillaPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  const heroImages = [
  '/assests/images/img2.jpg',
  '/assests/images/img21.jpg',
  '/assests/images/img13.jpg',
  '/assests/images/img3.jpg',
  '/assests/images/img20.jpg',
  '/assests/images/img14.jpg',
  '/assests/images/img15.jpg',

];



  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

 const [currentImage, setCurrentImage] = useState(0);

useEffect(() => {
  const interval = setInterval(() => {
    setCurrentImage((prev) => (prev + 1) % heroImages.length);
  }, 4000);

  return () => clearInterval(interval);
}, []);


  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const openGallery = (idx: number) => {setGalleryIndex(idx);setGalleryOpen(true);};
  const closeGallery = () => setGalleryOpen(false);
  const prevGallery = () => setGalleryIndex((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  const nextGallery = () => setGalleryIndex((i) => (i + 1) % GALLERY_IMAGES.length);

  const waLink = (msg: string) => `https://wa.me/94711989004?text=${encodeURIComponent(msg)}`;

  // Section animation refs
  const statsRef = useIntersection();
  const roomsRef = useIntersection();
  const aboutRef = useIntersection();
  const amenitiesRef = useIntersection();
  const testimonialsRef = useIntersection();
  const galleryRef = useIntersection();
  const locationRef = useIntersection();
  const faqRef = useIntersection();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E6', fontFamily: 'Inter, sans-serif', color: '#1B2420' }}>

      {/* ── NAVBAR ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          backgroundColor: scrolled ? 'rgba(11, 29, 26, 0.97)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.3)' : 'none'
        }}>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button onClick={() => scrollTo('home')} className="flex items-center gap-2 group" aria-label="Shady Mango Villa home">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-lg font-bold"
                style={{ backgroundColor: '#C79A3D', color: '#0B1D1A' }}>
                
                🥭
              </div>
              <div className="text-left">
                <div className="font-serif font-bold text-white leading-tight text-2xl md:text-2xl">
                Shady Mango Villa
                </div>

                <div className="text-xs hidden sm:block" style={{ color: '#9DBDB6' }}>Sigiriya, Sri Lanka</div>
              </div>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7" aria-label="Main navigation">
              {['rooms', 'about', 'gallery', 'location'].map((id) =>
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-sm font-medium capitalize transition-colors duration-200 hover:text-white"
                style={{ color: 'rgba(252, 251, 251, 0.82)' }}>
                
                  {id === 'rooms' ? 'Rooms' : id.charAt(0).toUpperCase() + id.slice(1)}
                </button>
              )}
            </nav>

            {/* CTA */}
            <div className="flex items-center gap-3">
              <a
                href={waLink("Hi! I'd like to book a stay at Shady Mango Villa. Please share availability.")}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:opacity-90"
                style={{ backgroundColor: '#25D366', borderRadius: '100px', padding: '10px 20px' }}>
                
                <WhatsAppIcon />
                Book on WhatsApp
              </a>
              {/* Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg"
                style={{ color: 'rgba(255,255,255,0.9)' }}
                aria-label="Toggle mobile menu"
                aria-expanded={mobileMenuOpen}>
                
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  {mobileMenuOpen ?
                  <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> :

                  <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
                  }
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className="md:hidden overflow-hidden transition-all duration-300"
          style={{
            maxHeight: mobileMenuOpen ? '320px' : '0',
            backgroundColor: 'rgba(11, 29, 26, 0.98)'
          }}>
          
          <div className="px-4 py-4 flex flex-col gap-1">
            {['rooms', 'about', 'gallery', 'location'].map((id) =>
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-left py-3 px-4 rounded-xl text-sm font-medium capitalize transition-colors hover:bg-white/10"
              style={{ color: 'rgba(255,255,255,0.85)' }}>
              
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            )}
            <a
              href={waLink("Hi! I'd like to book a stay at Shady Mango Villa.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: '#25D366' }}>
              
              <WhatsAppIcon />
              Book on WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#16322E' }}>
        
        {/* Hero background image */}
     <div
  className="
    absolute inset-0
    bg-cover
    bg-center
    bg-no-repeat
    transition-opacity duration-1000
  "
  style={{
    backgroundImage: `url(${heroImages[currentImage]})`,
  }}
/>

<section
  id="home"
  className="relative min-h-screen flex items-center justify-center overflow-hidden"
  style={{ backgroundColor: '#16322E' }}
></section>
        
        {/* Overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,29,26,0.72) 0%, rgba(22,50,46,0.55) 50%, rgba(11,29,26,0.88) 100%)' }} aria-hidden="true" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center" style={{ paddingTop: '80px' }}>
          <div
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-6 px-4 py-2 rounded-full"
            style={{ backgroundColor: 'rgba(13, 15, 15, 0.82)', color: '#e6dcc8', border: '1px solid rgba(199,154,61,0.3)' }}>
            
            <span>🌿</span> Boutique Villa · Sigiriya, Sri Lanka
          </div>

          <h1
            className="font-serif font-semibold text-white mb-6 leading-tight"
            style={{ fontSize: 'clamp(2.8rem, 7vw, 5rem)', letterSpacing: '-0.02em', lineHeight: '1.05' }}>
            
            Your Jungle Retreat<br />
            <em className="not-italic" style={{ color: '#e9d3a7' }}>at Sigiriya</em>
          </h1>

         <p
  className="text-sm mb-10 max-w-2xl mx-auto leading-relaxed"
  style={{ color: 'rgba(255, 255, 255, 0.85)' }}
>
  Nestled in the lush greenery of Sigiriya, Shady Mango Villa offers 4 beautifully appointed rooms, complimentary breakfast, and warm Sri Lankan hospitality — steps from the iconic Sigiriya Rock Fortress.
</p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-16">
            <a
              href={waLink("Hi! I'd like to book a stay at Shady Mango Villa. Please share availability and rates.")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-semibold text-white transition-all duration-200 hover:scale-105 hover:shadow-lg"
              style={{ backgroundColor: '#25D366', borderRadius: '100px', padding: '16px 32px', fontSize: '15px' }}>
              
              <WhatsAppIcon />
              Book on WhatsApp
            </a>
            <button
              onClick={() => scrollTo('rooms')}
              className="flex items-center gap-2 font-semibold transition-all duration-200 hover:gap-3"
              style={{ color: 'rgba(255,255,255,0.88)', fontSize: '15px' }}>
              
              View Our Rooms <ArrowIcon />
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{ color: 'rgba(255,255,255,0.5)', animation: 'scrollBounce 2s ease-in-out infinite' }}
          aria-hidden="true">
          
          <span className="text-xs tracking-widest uppercase font-medium">Scroll</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section
        ref={statsRef.ref}
        className="py-8 md:py-10"
        style={{ backgroundColor: '#16322E' }}>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
            { icon: '⭐', value: '9.6 / 10', label: 'Booking.com Score' },
            { icon: '🏡', value: '4 Rooms', label: 'AC & Non-AC' },
            { icon: '📅', value: 'Since 2017', label: 'Established' },
            { icon: '🪨', value: '1.4 km', label: 'From Sigiriya Rock' }].
            map((stat, i) =>
            <div
              key={i}
              className="flex items-center gap-3 p-4 rounded-2xl transition-all duration-500"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                opacity: statsRef.visible ? 1 : 0,
                transform: statsRef.visible ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${i * 100}ms`
              }}>
              
                <span className="text-2xl">{stat.icon}</span>
                <div>
                  <div className="font-serif font-bold text-white text-lg leading-tight">{stat.value}</div>
                  <div className="text-xs" style={{ color: '#9DBDB6' }}>{stat.label}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── ROOMS ── */}
      <section id="rooms" className="py-20 md:py-28" style={{ backgroundColor: '#F6F1E6' }}>
        <div
          ref={roomsRef.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section header */}
          <div
            className="text-center mb-14 transition-all duration-700"
            style={{ opacity: roomsRef.visible ? 1 : 0, transform: roomsRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>Accommodation</div>
            <h2 className="font-serif font-bold mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: '#16322E' }}>
              Rooms & Suites in Sigiriya
            </h2>
            <p className="max-w-2xl mx-auto text-base leading-relaxed" style={{ color: '#4A5E58' }}>
              Each room at Shady Mango Villa is a peaceful sanctuary — surrounded by tropical gardens, filled with natural light, and designed for comfort. Every stay includes a complimentary breakfast.
            </p>
          </div>

          {/* Room cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ROOMS.map((room, i) =>
            <RoomCard
              key={room.id}
              room={room}
              index={i}
              visible={roomsRef.visible}
              waLink={waLink} />

            )}
          </div>

          <p
            className="text-center mt-10 text-sm"
            style={{ color: '#4A5E58', opacity: roomsRef.visible ? 1 : 0, transition: 'opacity 0.7s ease 0.6s' }}>
            
            Children are welcome. Contact us for airport transfers or custom packages —{' '}
            <a
              href={waLink("Hi! I'd like to know more about packages at Shady Mango Villa.")}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-2"
              style={{ color: '#16322E' }}>
              
              just ask on WhatsApp
            </a>.
          </p>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-20 md:py-28" style={{ backgroundColor: '#E4EDE9' }}>
        <div
          ref={aboutRef.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Images */}
            <div
              className="relative transition-all duration-700"
              style={{ opacity: aboutRef.visible ? 1 : 0, transform: aboutRef.visible ? 'translateX(0)' : 'translateX(-30px)' }}>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden aspect-[3/4]">
                  <img
                    src='/assests/images/img6.jpg'
                    alt="Shady Mango Villa room interior with wooden walls"
                    className="w-full h-full object-cover" />
                  
                </div>
                <div className="flex flex-col gap-4">
                  <div className="rounded-2xl overflow-hidden aspect-square">
                    <img
                       src='/assests/images/img5.jpg'
                      alt="Shady Mango Villa breakfast spread with coffee"
                      className="w-full h-full object-cover" />
                    
                  </div>
                  <div
                    className="rounded-2xl p-5 flex flex-col items-center justify-center text-center"
                    style={{ backgroundColor: '#16322E', flex: 1 }}>
                    
                    <div className="font-serif font-bold text-white text-3xl">7+</div>
                    <div className="text-xs mt-1" style={{ color: '#9DBDB6' }}>Years of Warmth</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text */}
            <div
              className="transition-all duration-700 delay-200"
              style={{ opacity: aboutRef.visible ? 1 : 0, transform: aboutRef.visible ? 'translateX(0)' : 'translateX(30px)' }}>
              
              <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>Our Story</div>
              <h2 className="font-serif font-bold mb-6" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#16322E', lineHeight: '1.15' }}>
                A Family Villa in Sigiriya, Built With Heart
              </h2>
              <p className="mb-4 leading-relaxed" style={{ color: '#3A4E48', fontSize: '15px' }}>
                Shady Mango Villa was born from a simple dream — to share the magic of Sigiriya with travellers from around the world. Since 2017, our family has welcomed guests into our home, offering genuine Sri Lankan hospitality, home-cooked breakfasts, and a peaceful retreat surrounded by tropical nature.
              </p>
              <p className="mb-4 leading-relaxed" style={{ color: '#3A4E48', fontSize: '15px' }}>
                Nestled in the village of Kalapuraya, just 1.4 km from the iconic Sigiriya Rock Fortress, our villa is the perfect base for exploring one of Sri Lanka's most breathtaking regions. Wake up to birdsong, enjoy a freshly prepared breakfast, and step out to discover ancient wonders.
              </p>
              <p className="mb-8 leading-relaxed" style={{ color: '#3A4E48', fontSize: '15px' }}>
                With 4 comfortable rooms (AC and non-AC), free WiFi, free parking, and a warm family atmosphere, Shady Mango Villa offers everything you need for an unforgettable stay in Sigiriya.
              </p>

              {/* Host signature */}
              <div className="flex items-center gap-4 p-4 rounded-2xl" style={{ backgroundColor: 'rgba(22,50,46,0.08)' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
                  style={{ backgroundColor: '#16322E', color: '#C79A3D' }}>
                  
                  🥭
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: '#16322E' }}>— The Shady Mango Villa Family</div>
                  <div className="text-xs" style={{ color: '#4A5E58' }}>Your hosts in Sigiriya, since 2017</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AMENITIES ── */}
      <section
        className="py-16 md:py-20"
        style={{ backgroundColor: '#16322E' }}>
        
        <div
          ref={amenitiesRef.ref}
          className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div
            className="text-center mb-10 transition-all duration-700"
            style={{ opacity: amenitiesRef.visible ? 1 : 0, transform: amenitiesRef.visible ? 'translateY(0)' : 'translateY(20px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#C79A3D' }}>Everything Included</div>
            <h2 className="font-serif font-bold text-white" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
              All the Comforts You Need
            </h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
            {AMENITIES.map((a, i) =>
            <div
              key={i}
              className="flex flex-col items-center gap-2 p-3 md:p-4 rounded-2xl text-center transition-all duration-500"
              style={{
                backgroundColor: 'rgba(255,255,255,0.07)',
                opacity: amenitiesRef.visible ? 1 : 0,
                transform: amenitiesRef.visible ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${i * 50}ms`
              }}>
              
                <span className="text-2xl">{a.icon}</span>
                <span className="text-xs font-medium leading-tight" style={{ color: '#9DBDB6' }}>{a.label}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#F6F1E6' }}>
        <div
          ref={testimonialsRef.ref}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div
            className="text-center mb-12 transition-all duration-700"
            style={{ opacity: testimonialsRef.visible ? 1 : 0, transform: testimonialsRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>Guest Reviews</div>
            <h2 className="font-serif font-bold mb-3" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#16322E' }}>
              What Our Guests Say
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((s) => <span key={s} className="text-yellow-500 text-lg">★</span>)}
              </div>
              <span className="font-bold text-lg" style={{ color: '#16322E' }}>9.6 / 10</span>
              <span className="text-sm" style={{ color: '#4A5E58' }}>· 403 reviews on Booking.com</span>
            </div>
          </div>

          {/* Testimonial carousel */}
          <div
            className="relative transition-all duration-700 delay-200"
            style={{ opacity: testimonialsRef.visible ? 1 : 0, transform: testimonialsRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="overflow-hidden rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: 'rgba(22, 50, 46, 0.15) 0px 10px 40px -10px' }}>
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                
                {TESTIMONIALS.map((t, i) =>
                <div key={i} className="min-w-full p-8 md:p-12">
                    <div className="flex gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map((s) => <span key={s} className="text-yellow-500">★</span>)}
                    </div>
                    <blockquote
                    className="font-serif italic mb-6 leading-relaxed"
                    style={{ fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', color: '#16322E' }}>
                    
                      &ldquo;{t.text}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-3">
                      <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: '#16322E' }}>
                      
                        {t.name[0]}
                      </div>
                      <div>
                        <div className="font-semibold text-sm" style={{ color: '#16322E' }}>{t.name}</div>
                        <div className="text-xs" style={{ color: '#4A5E58' }}>{t.flag} {t.country}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {TESTIMONIALS.map((_, i) =>
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === activeTestimonial ? '24px' : '8px',
                  height: '8px',
                  backgroundColor: i === activeTestimonial ? '#16322E' : '#9DBDB6'
                }}
                aria-label={`Go to testimonial ${i + 1}`} />

              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section id="gallery" className="py-20 md:py-28" style={{ backgroundColor: '#E4EDE9' }}>
        <div
          ref={galleryRef.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div
            className="text-center mb-12 transition-all duration-700"
            style={{ opacity: galleryRef.visible ? 1 : 0, transform: galleryRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>Photo Gallery</div>
            <h2 className="font-serif font-bold mb-3" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#16322E' }}>
              See It for Yourself
            </h2>
            <p className="text-sm" style={{ color: '#4A5E58' }}>Tap any image to explore Shady Mango Villa in full</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {GALLERY_IMAGES.map((img, i) =>
            <button
              key={i}
              onClick={() => openGallery(i)}
              className="group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500"
              style={{
                aspectRatio: img.aspect === 'portrait' ? '3/4' : img.aspect === 'landscape' ? '4/3' : '1/1',
                gridColumn: i === 0 ? 'span 2' : 'span 1',
                gridRow: i === 0 ? 'span 2' : 'span 1',
                opacity: galleryRef.visible ? 1 : 0,
                transform: galleryRef.visible ? 'scale(1)' : 'scale(0.95)',
                transitionDelay: `${i * 60}ms`
              }}
              aria-label={`View ${img.alt}`}>
              
                <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16322E" strokeWidth="2.5">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── LOCATION ── */}
      <section id="location" className="py-20 md:py-28" style={{ backgroundColor: '#F6F1E6' }}>
        <div
          ref={locationRef.ref}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div
            className="text-center mb-12 transition-all duration-700"
            style={{ opacity: locationRef.visible ? 1 : 0, transform: locationRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>Find Us</div>
            <h2 className="font-serif font-bold mb-3" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#16322E' }}>
              Steps from Sigiriya Rock
            </h2>
            <p className="max-w-xl mx-auto text-sm leading-relaxed" style={{ color: '#4A5E58' }}>
              Shady Mango Villa is perfectly located in Kalapuraya village, just minutes from Sigiriya's most iconic landmarks.
            </p>
          </div>

          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-700 delay-200"
            style={{ opacity: locationRef.visible ? 1 : 0, transform: locationRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            {/* Map */}
            <div className="rounded-3xl overflow-hidden" style={{ height: '400px', boxShadow: 'rgba(22, 50, 46, 0.2) 0px 10px 30px -10px' }}>
             <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3951.5223638654816!2d80.76036027374143!3d7.94484670501903!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3afca12b206b7f71%3A0x75acb670e4f4cd20!2sShady%20Mango%20Villa!5e0!3m2!1sen!2slk!4v1786520339293!5m2!1sen!2slk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title="Shady Mango Villa exact location map"
/>
            </div>

            {/* Location details */}
            <div className="flex flex-col gap-6">
              {/* Address */}
              <div className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: 'rgba(22, 50, 46, 0.1) 0px 4px 20px -4px' }}>
                <div className="flex items-start gap-3">
                  <span className="text-2xl mt-0.5">📍</span>
                  <div>
                    <div className="font-semibold mb-1" style={{ color: '#16322E' }}>116/A/01 Kalapuraya, Sigiriya</div>
                    <div className="text-sm" style={{ color: '#4A5E58' }}>21121 Sigiriya, Sri Lanka</div>
                  </div>
                </div>
              </div>

              {/* Nearby */}
              <div className="p-5 rounded-2xl" style={{ backgroundColor: '#FFFFFF', boxShadow: 'rgba(22, 50, 46, 0.1) 0px 4px 20px -4px' }}>
                <h3 className="font-semibold mb-4 text-sm" style={{ color: '#16322E' }}>Nearby Attractions</h3>
                <ul className="space-y-3">
                  {NEARBY.map((n, i) =>
                  <li key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#C79A3D' }} />
                        <span className="text-sm" style={{ color: '#3A4E48' }}>{n.place}</span>
                      </div>
                      <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: '#E4EDE9', color: '#16322E' }}>{n.distance}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Directions CTA */}
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=7.9573,80.7597"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-4 px-6 rounded-2xl font-semibold text-sm text-white transition-all duration-200 hover:scale-105"
                style={{ backgroundColor: '#16322E' }}>
                
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 11 22 2 13 21 11 13 3 11" />
                </svg>
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#E4EDE9' }}>
        <div
          ref={faqRef.ref}
          className="max-w-3xl mx-auto px-4 sm:px-6">
          
          <div
            className="text-center mb-12 transition-all duration-700"
            style={{ opacity: faqRef.visible ? 1 : 0, transform: faqRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            <div className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#C79A3D' }}>FAQs</div>
            <h2 className="font-serif font-bold" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#16322E' }}>
              Frequently Asked Questions
            </h2>
          </div>

          <div
            className="space-y-3 transition-all duration-700 delay-200"
            style={{ opacity: faqRef.visible ? 1 : 0, transform: faqRef.visible ? 'translateY(0)' : 'translateY(30px)' }}>
            
            {FAQS.map((faq, i) =>
            <div
              key={i}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: '#FFFFFF', boxShadow: 'rgba(22, 50, 46, 0.08) 0px 4px 16px -4px' }}>
              
                <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left transition-colors duration-200 hover:bg-black/5">
                
                  <span className="font-semibold text-sm pr-4" style={{ color: '#16322E' }}>{faq.question}</span>
                  <ChevronIcon open={openFaq === i} />
                </button>
                <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: openFaq === i ? '200px' : '0' }}>
                
                  <p className="px-5 pb-5 text-sm leading-relaxed" style={{ color: '#4A5E58' }}>{faq.answer}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ backgroundColor: '#16322E' }}>
        
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('https://cf.bstatic.com/xdata/images/hotel/max300/786810880.jpg?k=6b608eac8fca9eb3f7cb50e50f8939dda72979a40be611ba6e7fb758d1fe1c28&o=')` }}
          aria-hidden="true" />
        
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(22,50,46,0.9) 0%, rgba(11,29,26,0.95) 100%)' }} aria-hidden="true" />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: '#C79A3D' }}>Reserve Your Stay</div>
          <h2 className="font-serif font-bold text-white mb-5" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.15' }}>
            Ready for Your Sigiriya Adventure?
          </h2>
          <p className="mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
            Reach out to us directly — we'll personally help you choose the right room and ensure every detail of your stay is perfect.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={waLink("Hi! I'd like to book a stay at Shady Mango Villa. Can you share availability and rates?")}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-semibold text-white transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#25D366', borderRadius: '100px', padding: '16px 32px', fontSize: '15px' }}>
              
              <WhatsAppIcon />
              WhatsApp Booking
            </a>
            <a
              href="tel:+94716251476"
              className="flex items-center gap-2 font-semibold transition-all duration-200 hover:scale-105"
              style={{ color: 'rgba(255,255,255,0.9)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: '100px', padding: '15px 32px', fontSize: '15px' }}>
              
              <PhoneIcon />
              Call Now
            </a>
          </div>

          <p className="mt-8 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Direct line &amp; WhatsApp: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>0711 989 004</strong>
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: '#0B1D1A', color: '#9DBDB6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg" style={{ backgroundColor: '#C79A3D', color: '#0B1D1A' }}>🥭</div>
                <div>
                  <div className="font-serif font-bold text-white text-lg">Shady Mango Villa</div>
                  <div className="text-xs" style={{ color: '#9DBDB6' }}>"Nature, Comfort & Warmth"</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#9DBDB6' }}>
                A boutique family villa in the heart of Sigiriya — where tropical nature, warm hospitality, and the magic of ancient Sri Lanka come together.
              </p>
            </div>

            {/* Explore */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm tracking-wide">Explore</h4>
              <ul className="space-y-2">
                {[['rooms', 'Rooms & Rates'], ['about', 'Our Story'], ['gallery', 'Gallery'], ['location', 'Location']].map(([id, label]) =>
                <li key={id}>
                    <button
                    onClick={() => scrollTo(id)}
                    className="text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#9DBDB6' }}>
                    
                      {label}
                    </button>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm tracking-wide">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href={waLink("Hi! I'd like to book a stay at Shady Mango Villa.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#9DBDB6' }}>
                    
                    <WhatsAppIcon />
                    WhatsApp: 0711 989 004
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+94716251476"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#9DBDB6' }}>
                    
                    <PhoneIcon />
                    Call: 0716 251 476
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:Shadymangovilla@gmail.com"
                    className="flex items-center gap-2 text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#9DBDB6' }}>
                    
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    Shadymangovilla@gmail.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=7.9573,80.7597"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: '#9DBDB6' }}>
                    
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    116/A/01 Kalapuraya, Sigiriya
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"
            style={{ borderTop: '1px solid rgba(157,189,182,0.15)', color: '#9DBDB6' }}>
            
            <span>© 2025 Shady Mango Villa. All rights reserved.</span>
            <span style={{ color: 'rgba(157,189,182,0.6)' }}>Established 2017 · Sigiriya, Sri Lanka</span>
          </div>
        </div>
      </footer>

      {/* ── FLOATING WHATSAPP ── */}
      <a
        href={waLink("Hi! I'd like to book a stay at Shady Mango Villa.")}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 font-semibold text-white text-sm shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl"
        style={{ backgroundColor: '#25D366', borderRadius: '100px', padding: '14px 20px' }}
        aria-label="Chat on WhatsApp">
        
        <WhatsAppIcon />
        <span className="hidden sm:inline">Chat with Us</span>
      </a>

      {/* ── GALLERY LIGHTBOX ── */}
      {galleryOpen &&
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
        onClick={closeGallery}>
        
          <div className="relative max-w-5xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button
            onClick={closeGallery}
            className="absolute -top-12 right-0 text-white text-3xl font-light hover:opacity-70 transition-opacity"
            aria-label="Close image viewer">
            
              ×
            </button>
            <img
            src={GALLERY_IMAGES[galleryIndex].src}
            alt={GALLERY_IMAGES[galleryIndex].alt}
            className="w-full max-h-[80vh] object-contain rounded-2xl" />
          
            <button
            onClick={prevGallery}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-colors"
            aria-label="Previous image">
            
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
            onClick={nextGallery}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-colors"
            aria-label="Next image">
            
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
            <p className="text-center text-white/60 text-sm mt-4">{galleryIndex + 1} / {GALLERY_IMAGES.length}</p>
          </div>
        </div>
      }

      {/* ── GLOBAL STYLES ── */}
      <style>{`
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
      `}</style>
    </div>);

}

// ─── Room Card Component ──────────────────────────────────────────────────────
function RoomCard({
  room,
  index,
  visible,
  waLink





}: {room: Room;index: number;visible: boolean;waLink: (msg: string) => string;}) {
  const [hovered, setHovered] = useState(false);

  return (
    <article
      className="rounded-3xl overflow-hidden transition-all duration-500"
      style={{
        backgroundColor: '#FFFFFF',
        boxShadow: hovered ?
        'rgba(22, 50, 46, 0.35) 0px 20px 40px -12px' :
        'rgba(22, 50, 46, 0.15) 0px 10px 30px -12px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${index * 120}ms`
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Room image */}
      <div className="relative overflow-hidden" style={{ height: '260px' }}>
        <img
          src={room.image}
          alt={room.alt}
          className="w-full h-full object-cover transition-transform duration-700"
          style={{ transform: hovered ? 'scale(1.06)' : 'scale(1)' }} />
        
        <div
          className="absolute top-4 left-4 text-xs font-semibold px-3 py-1.5 rounded-full"
          style={{ backgroundColor: 'rgba(22,50,46,0.85)', color: '#C79A3D', backdropFilter: 'blur(8px)' }}>
          
          {room.badge}
        </div>
      </div>

      {/* Room content */}
      <div className="p-6">
        <h3 className="font-serif font-bold mb-1" style={{ fontSize: '1.4rem', color: '#16322E' }}>{room.title}</h3>
        <p className="text-xs font-medium mb-3" style={{ color: '#C79A3D' }}>{room.guests}</p>
        <p className="text-sm leading-relaxed mb-5" style={{ color: '#4A5E58' }}>{room.description}</p>

        {/* Amenities */}
        <div className="flex flex-wrap gap-2 mb-6">
          {room.amenities.map((a, i) =>
          <span
            key={i}
            className="text-xs px-2.5 py-1 rounded-full font-medium"
            style={{ backgroundColor: '#E4EDE9', color: '#16322E' }}>
            
              {a}
            </span>
          )}
        </div>

        {/* Book button */}
        <a
          href={waLink(room.whatsappMsg)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-full font-semibold text-sm text-white transition-all duration-200 hover:scale-105 hover:shadow-lg"
          style={{ backgroundColor: '#25D366' }}>
          
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Book This Room
        </a>
      </div>
    </article>);

}